import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSidebarItems, flattenSidebarItemsForSearch } from './config/SidebarItems';
import { useAuth } from '../../contexts/AuthContext';
import { useAccountVesselScope } from '../../hooks/useAccountVesselScope';
import { useModuleEntitlements } from '../../modules/admin/module-management';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';

type PageSearchItem = {
  path: string;
  label: string;
  group?: string;
  icon?: React.ReactNode;
};

const CombinedSearch: React.FC = () => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { user, can, permissionsLoaded } = useAuth();
  const vesselAccountId = useAccountVesselScope();
  const { entitlements } = useModuleEntitlements();
  const canView = useMemo(
    () => (permissionsLoaded || user?.role === 'ADMIN' ? (moduleKey: string) => can(moduleKey, 'view') : undefined),
    [can, permissionsLoaded, user?.role]
  );

  const flatItems = useMemo(() => {
    const items = getSidebarItems(canView, vesselAccountId, entitlements);
    return flattenSidebarItemsForSearch(items);
  }, [canView, vesselAccountId, entitlements]);

  const filteredPages = useMemo<PageSearchItem[]>(() => {
    if (!query.trim()) return flatItems.slice(0, 8);
    const q = query.trim().toLowerCase();
    return flatItems.filter(
      (entry) => entry.label.toLowerCase().includes(q) || (entry.group && entry.group.toLowerCase().includes(q))
    );
  }, [flatItems, query]);

  const selectPath = useCallback(
    (path: string) => {
      navigate(path);
      setQuery('');
      setOpen(false);
      setHighlightIndex(0);
    },
    [navigate]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (open && filteredPages.length > 0) {
        const item = filteredPages[highlightIndex];
        if (item) selectPath(item.path);
      }
      return;
    }
    if (!open) return;
    if (filteredPages.length === 0) {
      if (e.key === 'Escape') setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => (i + 1) % filteredPages.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => (i - 1 + filteredPages.length) % filteredPages.length);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setHighlightIndex(0);
    }
  };

  useEffect(() => {
    setHighlightIndex(0);
    setOpen(query.length > 0);
  }, [query]);

  useEffect(() => {
    if (open && listRef.current && filteredPages.length) {
      const el = listRef.current.querySelector(`[data-index="${highlightIndex}"]`);
      el?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIndex, open, filteredPages.length]);

  useEffect(() => {
    const handleClickOutside = (ev: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(ev.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
        if (query.length > 0) setHighlightIndex(0);
      }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [query.length]);

  const showDropdown = open && (filteredPages.length > 0 || query.trim().length > 0);

  return (
    <div ref={containerRef} className="relative w-full max-w-xl">
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 dark:text-gray-500">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </span>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => query.length > 0 && setOpen(true)}
          placeholder="Search pages…"
          className={cn(
            'w-full pl-9 py-2 rounded-lg text-sm border transition-colors',
            query.length > 0 ? 'pr-9' : 'pr-4',
            colors.surface,
            colors.border,
            colors.text,
            'placeholder:text-gray-400 dark:placeholder:text-gray-500',
            'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
          )}
          aria-label="Search pages"
          aria-expanded={open}
          aria-autocomplete="list"
          role="combobox"
        />
        {query.length > 0 && (
          <button
            type="button"
            onClick={() => {
              setQuery('');
              setOpen(false);
              setHighlightIndex(0);
              inputRef.current?.focus();
            }}
            className={cn(
              'absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md transition-colors',
              colors.textMuted,
              'hover:bg-black/5 dark:hover:bg-white/5 hover:text-gray-700 dark:hover:text-gray-200',
              'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1'
            )}
            aria-label="Clear search"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
      {showDropdown && (
        <div
          ref={listRef}
          role="listbox"
          className={cn(
            'absolute left-0 right-0 top-full mt-1 max-h-80 overflow-auto rounded-lg border shadow-lg z-50',
            colors.surface,
            colors.border
          )}
        >
          {filteredPages.length === 0 ? (
            <div className={cn('px-4 py-3 text-sm', colors.textMuted)}>
              {query.trim() ? 'No pages match.' : 'Type to search pages.'}
            </div>
          ) : (
            <>
              <div className={cn('px-3 pt-2 pb-1', colors.border, 'border-b')}>
                <p className={cn('text-[11px] font-semibold uppercase tracking-wider', colors.textMuted)}>Pages</p>
              </div>
              {filteredPages.map((item, index) => (
                <button
                  key={`page-${item.path}-${index}`}
                  type="button"
                  role="option"
                  data-index={index}
                  aria-selected={index === highlightIndex}
                  onClick={() => selectPath(item.path)}
                  onMouseEnter={() => setHighlightIndex(index)}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors',
                    index === highlightIndex
                      ? 'bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-200'
                      : colors.text
                  )}
                >
                  {item.icon ? (
                    <span
                      className={cn(
                        'flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg',
                        index === highlightIndex ? 'bg-indigo-200/50 dark:bg-indigo-500/30' : 'bg-black/5 dark:bg-white/5'
                      )}
                    >
                      {item.icon}
                    </span>
                  ) : (
                    <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-black/5 dark:bg-white/5">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </span>
                  )}
                  <span className="flex-1 truncate font-medium">{item.label}</span>
                  {item.group && (
                    <span className={cn('text-xs truncate shrink-0', colors.textMuted)}>{item.group}</span>
                  )}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CombinedSearch;
